/**
 * proofs/api.ts — sổ bằng chứng của đơn.
 *
 * Endpoints:
 *   GET  /orders/{id}/proofs
 *   POST /orders/{id}/proofs   (ảnh upload qua /notes/upload trước, gửi URL)
 *   POST /orders/{id}/status   (thợ chụp tự giao đơn ảnh thẻ)
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api/client";
import { serverErrorMessage } from "@/lib/api/error-message";
import { downscaleImage } from "@/lib/image-downscale";
import { uploadImageToR2 } from "@/lib/upload";
import type { OrderProof, ProofKind } from "./utils";

export type { OrderProof, ProofKind } from "./utils";

export const proofKeys = {
  byOrder: (orderId: string) => ["proofs", orderId] as const,
};

/**
 * apiErrorMessage — lấy câu tiếng Việt server gửi kèm mã lỗi. Với 2 mã
 * proof_required_* thì thông điệp của server chính là hướng dẫn cho nhân
 * viên, đừng nuốt mất nó bằng một câu chung chung.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  return serverErrorMessage(error, fallback);
}

export function useOrderProofs(orderId: string | null | undefined) {
  return useQuery({
    queryKey: proofKeys.byOrder(orderId ?? ""),
    enabled: !!orderId,
    queryFn: async () => {
      const { data, error } = await api.GET("/orders/{id}/proofs", {
        params: { path: { id: orderId! } },
      });
      if (error || !data) {
        throw new Error(apiErrorMessage(error, "Không tải được bằng chứng của đơn"));
      }
      return data.items as OrderProof[];
    },
    staleTime: 15_000,
  });
}

type AddProofInput = {
  orderId: string;
  kind: ProofKind;
  files: File[];
  note?: string;
  /** Ảnh trước/sau của mặt thứ mấy (chấm QC bản 3, 2a). Vắng = Mặt 1. */
  face?: number;
  /** Báo tấm thứ mấy đang gửi (tính từ 1) — hộp chụp hiện "Đang gửi ảnh 1/2…". */
  onProgress?: (current: number, total: number) => void;
};

/**
 * Thu nhỏ rồi upload từng ảnh lấy URL công khai, sau đó ghi vào sổ — server
 * đóng dấu người chụp.
 *
 * Vì sao thu nhỏ (đo log prod 29–30/09/2026): ảnh camera gửi nguyên gốc
 * 4032×3024 / 5712×4284, 2–10 MB. Máy chủ xử lý ~1 giây, nhưng điện thoại ở
 * cửa hàng đẩy lên chỉ 15–130 KB/giây → một tấm 30–90 giây, nhân viên tưởng
 * treo rồi bấm gửi lại 3–4 lần. Cạnh dài 1600px (đúng cỡ khung xem lớn `lg`)
 * còn ~200–400 KB. Thu nhỏ hỏng thì `downscaleImage` trả lại tệp gốc — không
 * bao giờ vì tối ưu mà mất ảnh.
 */
export function useAddProof() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ orderId, kind, files, note, face, onProgress }: AddProofInput) => {
      const images: string[] = [];
      for (const [i, f] of files.entries()) {
        onProgress?.(i + 1, files.length);
        const up = await uploadImageToR2(await downscaleImage(f));
        images.push(up.public_url);
      }
      const { data, error } = await api.POST("/orders/{id}/proofs", {
        params: { path: { id: orderId } },
        body: { kind, images, ...(note ? { note } : {}), ...(face && face > 1 ? { face } : {}) },
      });
      if (error || !data) {
        throw new Error(apiErrorMessage(error, "Gửi ảnh bằng chứng thất bại — thử lại"));
      }
      return data as OrderProof;
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: proofKeys.byOrder(vars.orderId) });
      void qc.invalidateQueries({ queryKey: ["ekip", "schedule"] });
    },
  });
}

/**
 * Gỡ MỘT tấm khỏi bản ghi (tấm cuối đi thì bản ghi đi theo). Ai được gỡ do máy
 * chủ quyết — màn chỉ vẽ nút khi `proof.removable` bật.
 */
export function useDeleteProof() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ proofId, image }: { proofId: string; orderId: string; image: string }) => {
      const { data, error } = await api.DELETE("/proofs/{id}", {
        params: { path: { id: proofId }, query: { image } },
      });
      if (error || !data) {
        throw new Error(apiErrorMessage(error, "Xoá ảnh thất bại — thử lại"));
      }
      return data;
    },
    onSuccess: (_d, vars) => {
      void qc.invalidateQueries({ queryKey: proofKeys.byOrder(vars.orderId) });
      // Gỡ tấm cuối là cặp trước/sau hết đủ → thẻ ca và nhãn vàng "thiếu ảnh
      // nghiệm thu" trên danh sách đơn phải đọc lại.
      void qc.invalidateQueries({ queryKey: ["ekip", "schedule"] });
      void qc.invalidateQueries({ queryKey: ["orders", "attention-flags"] });
    },
  });
}

/**
 * useDeliverOrder — thợ chụp xác nhận đã giao đơn ảnh thẻ.
 *
 * Cố ý KHÔNG dùng lại useTransitionOrder của features/orders: trang đơn sẽ
 * import ngược feature này, nên gọi thẳng endpoint ở đây tránh vòng import.
 * Hành vi (endpoint + invalidate) giữ y như useTransitionOrder.
 */
export function useDeliverOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (orderId: string) => {
      const { data, error } = await api.POST("/orders/{id}/status", {
        params: { path: { id: orderId } },
        body: { status: "delivered" },
      });
      if (error || !data) {
        throw new Error(
          apiErrorMessage(error, "Xác nhận giao thất bại — đã gửi ảnh bằng chứng chưa?"),
        );
      }
      return data;
    },
    onSuccess: (_d, orderId) => {
      void qc.invalidateQueries({ queryKey: ["orders"] });
      void qc.invalidateQueries({ queryKey: proofKeys.byOrder(orderId) });
      void qc.invalidateQueries({ queryKey: ["ekip", "schedule"] });
    },
  });
}
